import { useState } from 'react'
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { Modal, ModalContent, ModalFooterWithActions, ModalFooterWithActionsType } from '@/components/modal'

export type OrgAdminConfirmationDialogProps = {
  title: string
  message: string
  visible: boolean
  successMessage?: string
  destructive?: boolean
  onConfirm: () => Promise<void>
  closeDialog: () => void
  refreshData: () => void
  /** Raise modal above another open modal. Default: false. */
  stacked?: boolean
}

export default function OrgAdminConfirmationDialog({
  title,
  message,
  visible,
  successMessage,
  destructive = false,
  onConfirm,
  closeDialog,
  refreshData,
  stacked = false,
}: OrgAdminConfirmationDialogProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)

  async function handleConfirm() {
    setLoading(true)
    setError(null)
    try {
      await onConfirm()
      if (successMessage) {
        setShowSuccess(true)
      } else {
        closeDialog()
      }
    } catch (err) {
      const status = (err as FetchBaseQueryError).status
      if (status === 404) {
        setError('404: This resource may have already been deleted.')
      } else if (status === 410) {
        setError('This resource may have already been completed.')
        await new Promise((resolve) => setTimeout(resolve, 1000))
        await refreshData()
      } else if (status === 429) {
        setError('Please wait a moment before trying again.')
      } else {
        setError('There was an error processing your request.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={visible} onOpenChange={(open) => !open && closeDialog()}>
      <ModalContent
        title={title}
        width="md"
        height="max-sm"
        stacked={stacked}
        preventDismiss={loading}
        footer={
          showSuccess ? (
            <ModalFooterWithActions primaryAction={{ label: 'Close', onClick: closeDialog }} />
          ) : (
            <ModalFooterWithActions
              type={destructive ? ModalFooterWithActionsType.DESTRUCTIVE : ModalFooterWithActionsType.DEFAULT}
              secondaryAction={{ label: 'Cancel', onClick: closeDialog, disabled: loading }}
              primaryAction={{ label: 'Confirm', onClick: handleConfirm, loading, disabled: loading }}
            />
          )
        }
      >
        <p className="text-sm whitespace-pre-wrap">{message}</p>
        {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
        {showSuccess && successMessage && <p className="mt-2 text-sm font-bold text-brand-700">{successMessage}</p>}
      </ModalContent>
    </Modal>
  )
}
